import { useTranslation } from '../i18n'

/**
 * A team membership role, as an identifier rather than display text.
 *
 * These ids are what permission gates compare against and what the localized
 * label is looked up from. Keeping the two jobs on one value is what left the
 * role badge showing English in every locale: the role used to *be* the string
 * "Owner", so there was nothing to translate — no key existed at all.
 */
export type TeamRoleId = 'owner' | 'admin' | 'member' | 'viewOnly'

/** Key in the `common` namespace holding each role's localized label. */
export const TEAM_ROLE_LABEL_KEYS: Record<TeamRoleId, string> = {
  owner: 'roles.owner',
  admin: 'roles.admin',
  member: 'roles.member',
  viewOnly: 'roles.viewOnly',
}

/**
 * Map a raw role string from the API onto a `TeamRoleId`.
 *
 * Tolerant on purpose. The backend has shipped two spellings for the same two
 * roles — `edit`/`view` alongside `member`/`viewOnly` — and group payloads carry
 * whichever the writing client used, so both must resolve. An unrecognised role
 * reads as `member`, which is the label this badge has always fallen back to;
 * the finer "may this user edit?" question is answered by `canEdit`, not here,
 * so a permissive label cannot grant a capability.
 */
export function normalizeTeamRoleId(role: string): TeamRoleId {
  switch (role.trim().toLowerCase()) {
    case 'owner':
      return 'owner'
    case 'admin':
      return 'admin'
    case 'view':
    case 'viewonly':
      return 'viewOnly'
    default:
      return 'member'
  }
}

/**
 * Resolve a role — either a `TeamRoleId` or a raw API role string — to its
 * localized label.
 *
 * Reads from the `common` namespace explicitly, so a component already scoped to
 * another namespace (the plan-hunt steps use `planHunt`/`planHuntDrives`) gets
 * the same wording without switching its own namespace.
 */
export function useTeamRoleLabel(): (role: string) => string {
  const { t } = useTranslation('common')
  return (role: string) => t(TEAM_ROLE_LABEL_KEYS[normalizeTeamRoleId(role)])
}
